import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adminCommitteeApi } from '../lib/api'
import type { SaveCommitteeRequest } from '../types/admin'

export function useAdminCommittees() {
  return useQuery({
    queryKey: ['admin', 'committees'],
    queryFn: async () => {
      const response = await adminCommitteeApi.getAll()
      return response.data
    },
  })
}

export function useAdminCommitteeDetail(id: string | undefined) {
  return useQuery({
    queryKey: ['admin', 'committees', id],
    queryFn: async () => {
      if (!id) throw new Error('Committee ID is required')
      const response = await adminCommitteeApi.getById(id)
      return response.data
    },
    enabled: !!id,
  })
}

export function useCreateCommittee() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: SaveCommitteeRequest) =>
      adminCommitteeApi.create(data).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'committees'] })
    },
  })
}

export function useUpdateCommittee(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: SaveCommitteeRequest) =>
      adminCommitteeApi.update(id, data).then(r => r.data),
    onSuccess: (data) => {
      queryClient.setQueryData(['admin', 'committees', id], data)
      queryClient.invalidateQueries({ queryKey: ['admin', 'committees'] })
    },
  })
}

export function useDeleteCommittee() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => adminCommitteeApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'committees'] })
    },
  })
}
